import { Badge, Box, HStack, RadioCard, Text } from '@chakra-ui/react';
import { Sparks } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import type { CollabTypeOption } from './collab-types';

interface CollabTypeCardProps {
  option: CollabTypeOption;
  /** A line about this kind for the creator proposed (her UGC starting price). */
  note?: string;
}

/**
 * One collaboration-type option in the type step, as a Chakra RadioCard item
 * (native radio semantics, keyboard nav, disabled handling for the "soon"
 * kinds). Selected state is a border + cream fill — no shadow. The lilac icon
 * tile is reserved for the sponsored kind; the rest use a neutral tile.
 */
const CollabTypeCard = ({ option, note }: CollabTypeCardProps) => {
  const { t } = useTranslation();
  const Icon = option.icon;
  const title = t(option.titleKey);
  const soonLabel = t('invitations.modal.collab.soon');
  const disabled = !option.enabled;
  const isLilac = option.type === 'sponsored';

  return (
    <RadioCard.Item
      value={option.type}
      disabled={disabled}
      colorPalette="primary"
      h="full"
      // The visible card border lives on ItemControl; neutralize the Item's own
      // recipe border/ring so the selected state shows a single clean contour
      // instead of a doubled outline.
      p="0"
      bg="transparent"
      borderWidth="0"
      boxShadow="none"
      _checked={{ borderWidth: '0', boxShadow: 'none' }}
      _disabled={{ opacity: 0.55, cursor: 'not-allowed' }}
    >
      <RadioCard.ItemHiddenInput aria-label={disabled ? `${title}, ${soonLabel}` : undefined} />
      <RadioCard.ItemControl
        position="relative"
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        h="full"
        p="16px"
        borderWidth="1px"
        borderColor="border"
        rounded="xl"
        bg="panelBg"
        _checked={{ borderColor: 'primary.900', bg: 'softCream' }}
      >
        <RadioCard.ItemIndicator position="absolute" top="14px" right="14px" />
        <Box
          boxSize="40px"
          rounded="lg"
          bg={isLilac ? 'lilac.100' : 'whiteSmoke'}
          color={isLilac ? 'lilac.700' : 'primary.500'}
          // Border keeps the tile visible on the cream selected-card background,
          // where a borderless whiteSmoke tile would vanish.
          borderWidth="1px"
          borderColor={isLilac ? 'lilac.200' : 'border'}
          display="grid"
          placeItems="center"
          flex="none"
        >
          <Icon width={19} height={19} />
        </Box>
        <HStack gap="2" mt="13px">
          <RadioCard.ItemText
            fontFamily="heading"
            fontSize="14px"
            fontWeight="semibold"
            color="dark"
          >
            {title}
          </RadioCard.ItemText>
          {option.badge === 'soon' && (
            <Badge colorPalette="gray" variant="subtle" size="sm">
              {soonLabel}
            </Badge>
          )}
          {option.badge === 'new' && (
            <Badge size="sm" colorPalette="lilac">
              {t('invitations.modal.collab.new')}
            </Badge>
          )}
        </HStack>
        <RadioCard.ItemDescription fontSize="12px" color="fg.muted" mt="5px" lineHeight="1.6">
          {t(option.descriptionKey)}
        </RadioCard.ItemDescription>
        {note && (
          <HStack
            gap="1.5"
            mt="10px"
            px="10px"
            py="6px"
            w="full"
            rounded="md"
            bg="whiteSmoke"
            borderWidth="1px"
            borderColor="border"
          >
            <Box as="span" color="lilac.600" flex="none" aria-hidden="true">
              <Sparks width={12} height={12} />
            </Box>
            <Text fontSize="11.5px" color="ink">
              {note}
            </Text>
          </HStack>
        )}
      </RadioCard.ItemControl>
    </RadioCard.Item>
  );
};

export default CollabTypeCard;
